import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { BackNav } from "@/components/BackNav";
import { Section, SectionRow } from "@/components/Section";
import { CenteredError, Spinner } from "@/components/Spinner";
import { PageLoadError, errorText } from "@/components/LockedPage";
import { Icon } from "@/components/Icon";
import { FOUNDER_LESSONS_LABEL, FounderInstagramCard } from "@/components/FounderInstagramCard";
import { LessonMaterialsCard } from "@/components/LessonMaterialsCard";
import type { IconName } from "@/components/Icon";
import { openExternalOnAnchorClick } from "@/lib/openExternal";
import {
  useKBArticles,
  useKBLessonCategories,
  useKBLessons,
  useKBLessonsAll,
  useKBSections,
  usePodcastsAccess,
  useVoteKBLesson,
  useMarkKBLessonViewed,
} from "@/api/hooks";
import type { KBLessonItemOut } from "@/api/types";
import { tg } from "@/lib/telegram";
import { showAppAlert } from "@/lib/appAlert";
import { sanitizeHtml } from "@/lib/sanitize";

/**
 * База знань — accordion layout.
 *
 * Одна сторінка з двома великими розділами що розгортаються inline:
 *   • Статті     → 3 категорії → список telegra.ph посилань
 *   • Уроки      → 7 категорій → список уроків → детальна сторінка
 * Плюс одна shortcut-кнопка:
 *   • Подкасти   → external Telegram channel link
 * І окремий блок внизу:
 *   • Стати спікером
 *
 * Lesson detail відкривається окремим view'ом (через setView).
 */

type View =
  | { kind: "browse" }
  | {
      kind: "lesson_detail";
      lessonId: number;
      // null — урок відкритий диплінком, картки зі списку ще немає
      fallback: KBLessonItemOut | null;
      categoryIdx: number | null;
      categoryName: string;
    };

// Persisted accordion state — переживає навігацію в межах /knowledge-base/*
// (стаття, урок), але **очищується** коли юзер виходить на /home, /quests тощо.
// Логіка: при unmount будь-якого KB-page компонента ми перевіряємо новий шлях;
// якщо вже НЕ у KB-дереві → wipe state.
export const KB_STATE_KEY = "inflow:kb:accordion";

export function clearKBStateIfLeavingTree(): void {
  if (!window.location.pathname.startsWith("/knowledge-base")) {
    try {
      sessionStorage.removeItem(KB_STATE_KEY);
    } catch {
      /* sessionStorage недоступний */
    }
  }
}

interface PersistedKBState {
  expandedArticles?: boolean;
  expandedLessons?: boolean;
  expandedSpeaker?: boolean;
  openedArticleCat?: string | null;
  openedLessonCat?: number | null;
}

function loadKBState(): PersistedKBState {
  try {
    const raw = sessionStorage.getItem(KB_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveKBState(state: PersistedKBState) {
  try {
    sessionStorage.setItem(KB_STATE_KEY, JSON.stringify(state));
  } catch {
    /* sessionStorage недоступний — ігноруємо */
  }
}

function usePersistedState<T>(
  key: keyof PersistedKBState,
  initial: T,
): [T, (v: T | ((p: T) => T)) => void] {
  const [val, setVal] = useState<T>(() => {
    const persisted = loadKBState();
    const v = persisted[key];
    return v === undefined ? initial : (v as T);
  });
  const setter = (next: T | ((p: T) => T)) => {
    setVal((prev) => {
      const newVal = typeof next === "function" ? (next as (p: T) => T)(prev) : next;
      const current = loadKBState();
      saveKBState({ ...current, [key]: newVal });
      return newVal;
    });
  };
  return [val, setter];
}

export function KnowledgeBasePage() {
  // Очищуємо persisted KB-state коли юзер виходить з дерева /knowledge-base/*
  // на головну, квести, тощо. Внутрішні переходи (article, lesson) — state
  // залишається.
  useEffect(() => {
    return () => clearKBStateIfLeavingTree();
  }, []);

  const [view, setView] = useState<View>({ kind: "browse" });
  // Стан розгорнутих секцій — sessionStorage щоб переживало навігацію
  // у /article/:slug та назад.
  const [expandedArticles, setExpandedArticles] = usePersistedState(
    "expandedArticles",
    false,
  );
  const [expandedLessons, setExpandedLessons] = usePersistedState(
    "expandedLessons",
    false,
  );
  const [expandedSpeaker, setExpandedSpeaker] = usePersistedState(
    "expandedSpeaker",
    false,
  );
  const [openedArticleCat, setOpenedArticleCat] = usePersistedState<string | null>(
    "openedArticleCat",
    null,
  );
  const [openedLessonCat, setOpenedLessonCat] = usePersistedState<number | null>(
    "openedLessonCat",
    null,
  );

  // Диплінк «?lesson=<id>» (owner 2026-09-12): кнопка в листі про новий урок
  // відкриває САМ урок. Категорія невідома — LessonDetailView дотягне список.
  useEffect(() => {
    const id = Number(new URLSearchParams(window.location.search).get("lesson"));
    if (!id) return;
    window.history.replaceState(null, "", "/knowledge-base");
    setView({
      kind: "lesson_detail",
      lessonId: id,
      fallback: null,
      categoryIdx: null,
      categoryName: "",
    });
  }, [setView]);

  if (view.kind === "lesson_detail") {
    return (
      <LessonDetailView
        lessonId={view.lessonId}
        fallback={view.fallback}
        categoryIdx={view.categoryIdx}
        onBack={() => setView({ kind: "browse" })}
      />
    );
  }

  return (
    <BrowseView
      onOpenLesson={(lesson, categoryIdx, categoryName) =>
        setView({
          kind: "lesson_detail",
          lessonId: lesson.id,
          fallback: lesson,
          categoryIdx,
          categoryName,
        })
      }
      expandedArticles={expandedArticles}
      setExpandedArticles={setExpandedArticles}
      expandedLessons={expandedLessons}
      setExpandedLessons={setExpandedLessons}
      expandedSpeaker={expandedSpeaker}
      setExpandedSpeaker={setExpandedSpeaker}
      openedArticleCat={openedArticleCat}
      setOpenedArticleCat={setOpenedArticleCat}
      openedLessonCat={openedLessonCat}
      setOpenedLessonCat={setOpenedLessonCat}
    />
  );
}

// ============================================================
// Browse — main page з аккордеонами
// ============================================================

function BrowseView({
  onOpenLesson,
  expandedArticles,
  setExpandedArticles,
  expandedLessons,
  setExpandedLessons,
  expandedSpeaker,
  setExpandedSpeaker,
  openedArticleCat,
  setOpenedArticleCat,
  openedLessonCat,
  setOpenedLessonCat,
}: {
  onOpenLesson: (
    lesson: KBLessonItemOut,
    categoryIdx: number,
    categoryName: string,
  ) => void;
  expandedArticles: boolean;
  setExpandedArticles: (v: boolean) => void;
  expandedLessons: boolean;
  setExpandedLessons: (v: boolean) => void;
  expandedSpeaker: boolean;
  setExpandedSpeaker: (v: boolean) => void;
  openedArticleCat: string | null;
  setOpenedArticleCat: (v: string | null) => void;
  openedLessonCat: number | null;
  setOpenedLessonCat: (v: number | null) => void;
}) {
  const sectionsQ = useKBSections();

  if (sectionsQ.isLoading) return <Spinner />;
  if (sectionsQ.isError) return <PageLoadError error={sectionsQ.error} section="knowledge_base" />;

  const data = sectionsQ.data!;
  const podcastsSection = data.sections.find((s) => s.id === "podcasts");
  const becomeSpeakerSection = data.sections.find(
    (s) => s.id === "become_speaker",
  );

  return (
    <>
      <BackNav />
      <PageHeader
        eyebrow="база знань"
        title="База знань"
        titleLead="Знання —"
        titleAccent="сила."
        scribbleWidth={110}
        subtitle="Обирай розділ, який тебе цікавий, щоб отримати доступ до матеріалів."
      />

      {/* Матеріали — головна картка з 3 рядками-аккордеонами */}
      <Section>
        {/* Articles row */}
        {(() => {
          const articlesSection = data.sections.find((s) => s.id === "articles");
          const articlesCount = articlesSection?.title.match(/\((\d+)\)/)?.[1];
          return (
            <AccordionRow
              index={1}
              icon="doc"
              title="Статті"
              meta={articlesCount}
              expanded={expandedArticles}
              onToggle={() => setExpandedArticles((v) => !v)}
            >
              <ArticlesBlock
                openedCat={openedArticleCat}
                onToggleCat={(idx) =>
                  setOpenedArticleCat((v) => (v === idx ? null : idx))
                }
              />
            </AccordionRow>
          );
        })()}

        {/* Podcasts row — через /community/podcasts: учасник → диплінк,
            не-учасник з підпискою → invite у спільноту (юзер 2026-06-13). */}
        {podcastsSection && (
          <PodcastsRow
            index={2}
            title={podcastsSection.title.replace(/\s*\(\d+\)\s*🎙️?/, "").trim()}
            meta={(() => {
              const m = podcastsSection.title.match(/\((\d+)\)/);
              return m ? m[1] : undefined;
            })()}
          />
        )}

        {/* Lessons row */}
        {(() => {
          const lessonsSection = data.sections.find((s) => s.id === "lessons");
          const lessonsCount = lessonsSection?.title.match(/\((\d+)\)/)?.[1];
          // Прогрес перегляду (owner 2026-09-17): показуємо «12 / 56», щойно
          // людина подивилась хоч один урок — інакше просто кількість.
          const viewed = lessonsSection?.viewed_count ?? 0;
          const totalLessons = lessonsSection?.total_count ?? null;
          const lessonsMeta =
            viewed > 0 && totalLessons
              ? `${viewed} / ${totalLessons}`
              : lessonsCount;
          return (
        <AccordionRow
          index={3}
          icon="play"
          title="Уроки від спікерів"
          meta={lessonsMeta}
          expanded={expandedLessons}
          onToggle={() => setExpandedLessons((v) => !v)}
          isLast
        >
          <LessonsBlock
            openedCat={openedLessonCat}
            onToggleCat={(idx) =>
              setOpenedLessonCat((v) => (v === idx ? null : idx))
            }
            onOpenLesson={onOpenLesson}
          />
        </AccordionRow>
          );
        })()}
      </Section>

      {/* Become speaker — окремий блок, що розкривається */}
      {becomeSpeakerSection && (
        <div className="mb-6 overflow-hidden rounded-ios-card bg-dark shadow-card-soft">
          <button
            type="button"
            onClick={() => setExpandedSpeaker((v) => !v)}
            className="flex w-full items-center gap-3 p-4 text-left active:bg-black/40 transition"
          >
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary text-dark">
              <Icon name="star" size={16} />
            </div>
            <div className="flex-1">
              <div className="font-display text-[0.875rem] font-semibold text-white">
                Стати спікером в inFLOW
              </div>
              <div className="mt-0.5 font-mono text-[0.65625rem] uppercase tracking-[0.08em] text-primary">
                поділись досвідом
              </div>
            </div>
            <Icon
              name="arrow-right"
              size={16}
              color="#FFFFFF"
              className={`transition-transform duration-200 ${
                expandedSpeaker ? "rotate-90" : ""
              }`}
            />
          </button>

          <div
            className={`grid transform-gpu transition-[grid-template-rows] duration-200 ${
              expandedSpeaker ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
            }`}
          >
            <div className="overflow-hidden">
              <div className="border-t border-white/10 px-4 pb-4 pt-4">
                {/* Текст з API (1.0 become_speacker_text). Telegram HTML
                    зберігається (b/i), білий фон під цитатами не потрібен —
                    рендеримо як білий текст на dark фоні. */}
                <div
                  className="text-[0.875rem] leading-relaxed text-white/85 [&_a]:underline"
                  onClick={openExternalOnAnchorClick}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(becomeSpeakerSection.text),
                  }}
                />
                {/* Контакт CTA — primary orange button */}
                <a
                  href={
                    becomeSpeakerSection.external_url ??
                    "https://t.me/inflow_manager"
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={openExternalOnAnchorClick}
                  className="mt-4 flex items-center justify-center gap-2 rounded-full bg-primary px-4 py-3 font-display text-[0.875rem] font-semibold text-dark active:scale-95 transition"
                >
                  <Icon name="chat" size={16} color="#1a0c02" />
                  Написати @inflow_manager
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================
// Accordion primitive
// ============================================================

function AccordionRow({
  index,
  icon,
  title,
  meta,
  expanded,
  onToggle,
  isLast,
  children,
}: {
  index?: number;
  icon: IconName;
  title: string;
  meta?: string;
  expanded: boolean;
  onToggle: () => void;
  isLast?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={isLast ? "" : "border-b border-line"}>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-3 py-3 text-left active:bg-surface-soft"
      >
        {index !== undefined && (
          <div className="font-mono text-[0.6875rem] tabular-nums text-ink-dim">
            {String(index).padStart(2, "0")}
          </div>
        )}
        <div className="grid h-8 w-8 place-items-center rounded-[0.625rem] bg-surface-soft text-primary">
          <Icon name={icon} size={16} />
        </div>
        <div className="flex-1 font-display text-[0.875rem] font-semibold text-ink">
          {title}
        </div>
        {meta && (
          <div className="font-mono text-[0.65625rem] text-ink-dim">{meta}</div>
        )}
        <Icon
          name="arrow-right"
          size={14}
          color="rgba(23,20,17,0.40)"
          className={`transition-transform duration-200 ${
            expanded ? "rotate-90" : ""
          }`}
        />
      </button>
      <div
        className={`grid transform-gpu transition-[grid-template-rows] duration-200 ${
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">{children}</div>
      </div>
    </div>
  );
}

// ============================================================
// External link row (для Подкастів)
// ============================================================

function ExternalRow({
  index,
  icon,
  title,
  meta,
  url,
}: {
  index: number;
  icon: IconName;
  title: string;
  meta?: string;
  url: string;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        tg()?.openTelegramLink?.(url) ?? window.open(url, "_blank");
      }}
      className="flex w-full items-center gap-3 border-b border-line px-3 py-3 text-left active:bg-surface-soft"
    >
      <div className="font-mono text-[0.6875rem] tabular-nums text-ink-dim">
        {String(index).padStart(2, "0")}
      </div>
      <div className="grid h-8 w-8 place-items-center rounded-[0.625rem] bg-surface-soft text-primary">
        <Icon name={icon} size={16} />
      </div>
      <div className="flex-1 font-display text-[0.875rem] font-semibold text-ink">
        {title}
      </div>
      {meta && (
        <div className="font-mono text-[0.65625rem] text-ink-dim">{meta}</div>
      )}
      <Icon name="arrow-right"
        size={14}
        color="rgba(23,20,17,0.40)" className="-rotate-45" />
    </button>
  );
}

// Рядок «Подкасти» → власний розділ у аппі (owner 2026-08-09). Раніше вів у
// закритий чат спільноти, тепер файли лежать у нас і грають прямо тут. Перехід
// у чат лишився запасним шляхом на випадок, якщо файл ще не викачаний.
function PodcastsRow({
  index,
  title,
  meta,
}: {
  index: number;
  title: string;
  meta?: string;
}) {
  const navigate = useNavigate();
  const podMut = usePodcastsAccess();
  const handle = async () => {
    if (podMut.isPending) return;
    tg()?.HapticFeedback?.selectionChanged?.();
    navigate("/podcasts");
  };
  return (
    <button
      type="button"
      onClick={handle}
      disabled={podMut.isPending}
      className="flex w-full items-center gap-3 border-b border-line px-3 py-3 text-left active:bg-surface-soft disabled:opacity-60"
    >
      <div className="font-mono text-[0.6875rem] tabular-nums text-ink-dim">
        {String(index).padStart(2, "0")}
      </div>
      <div className="grid h-8 w-8 place-items-center rounded-[0.625rem] bg-surface-soft text-primary">
        {/* Owner 2026-09-12: у подкастів був чат — тут мусить бути мікрофон. */}
        <Icon name="mic" size={16} />
      </div>
      <div className="flex-1 font-display text-[0.875rem] font-semibold text-ink">
        {title}
      </div>
      {meta && (
        <div className="font-mono text-[0.65625rem] text-ink-dim">{meta}</div>
      )}
      {/* Owner 2026-09-12: «друга стрілка завелика». Причина — у
          `arrow-up-right` bbox квадратний (12×12), а в `arrow-right`
          широкий (16×12): вікно viewBox робить квадрат по більшій стороні,
          тому діагональна заповнює всю висоту й читається крупнішою. Беремо
          ТУ САМУ стрілку під −45° — одна геометрія, однакова вага. */}
      <Icon
        name="arrow-right"
        size={14}
        color="rgba(23,20,17,0.40)"
        className="-rotate-45"
      />
    </button>
  );
}

// ============================================================
// Articles content — 3 категорії
// ============================================================

function ArticlesBlock({
  openedCat,
  onToggleCat,
}: {
  openedCat: string | null;
  onToggleCat: (idx: string) => void;
}) {
  const q = useKBArticles();
  if (q.isLoading) {
    return (
      <div className="px-3 py-4 text-center font-mono text-[0.65625rem] uppercase tracking-[0.08em] text-ink-dim">
        Завантажую…
      </div>
    );
  }
  if (q.isError) {
    return (
      <div className="px-3 py-4 text-[0.75rem] text-danger">
        {errorText(q.error)}
      </div>
    );
  }
  const cats = q.data!;

  return (
    <div className="px-3 pb-3">
      <div className="rounded-[0.875rem] bg-surface-soft">
        {cats.map((c, i) => {
          const isOpen = openedCat === c.idx;
          return (
            <div
              key={c.idx}
              className={i === cats.length - 1 ? "" : "border-b border-line"}
            >
              <button
                type="button"
                onClick={() => onToggleCat(c.idx)}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left active:bg-surface"
              >
                <div className="flex-1 font-display text-[0.8125rem] font-semibold text-ink">
                  {c.name}
                </div>
                <div className="font-mono text-[0.65625rem] text-ink-dim">
                  {c.items.length}
                </div>
                <Icon
                  name="arrow-right"
                  size={12}
                  color="rgba(23,20,17,0.40)"
                  className={`transition-transform duration-200 ${
                    isOpen ? "rotate-90" : ""
                  }`}
                />
              </button>
              <div
                className={`grid transform-gpu transition-[grid-template-rows] duration-200 ${
                  isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                }`}
              >
                <div className="overflow-hidden">
                  <div className="px-2 pb-2">
                    {c.items.map((a) => (
                      <Link
                        key={a.slug}
                        to={`/knowledge-base/article/${a.slug}`}
                        className="flex w-full items-center gap-2 rounded-[0.625rem] px-2 py-2 text-left active:bg-bg"
                      >
                        <Icon
                          name="arrow-right"
                          size={12}
                          color="#FE6B04"
                        />
                        <span className="flex-1 text-[0.8125rem] text-ink">
                          {a.title}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// Lessons content — 7 категорій
// ============================================================

function LessonsBlock({
  openedCat,
  onToggleCat,
  onOpenLesson,
}: {
  openedCat: number | null;
  onToggleCat: (idx: number) => void;
  onOpenLesson: (
    lesson: KBLessonItemOut,
    categoryIdx: number,
    categoryName: string,
  ) => void;
}) {
  const q = useKBLessonCategories();
  if (q.isLoading) {
    return (
      <div className="px-3 py-4 text-center font-mono text-[0.65625rem] uppercase tracking-[0.08em] text-ink-dim">
        Завантажую…
      </div>
    );
  }
  if (q.isError) {
    return (
      <div className="px-3 py-4 text-[0.75rem] text-danger">
        {errorText(q.error)}
      </div>
    );
  }
  const cats = q.data!;

  return (
    <div className="px-3 pb-3">
      <div className="rounded-[0.875rem] bg-surface-soft">
        {cats.map((c, i) => {
          const isOpen = openedCat === c.idx;
          return (
            <div
              key={c.idx}
              className={i === cats.length - 1 ? "" : "border-b border-line"}
            >
              <button
                type="button"
                onClick={() => onToggleCat(c.idx)}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left active:bg-surface"
              >
                <div className="flex-1 font-display text-[0.8125rem] font-semibold text-ink">
                  {c.name}
                </div>
                <div className="font-mono text-[0.65625rem] text-ink-dim">
                  {c.count}
                </div>
                <Icon
                  name="arrow-right"
                  size={12}
                  color="rgba(23,20,17,0.40)"
                  className={`transition-transform duration-200 ${
                    isOpen ? "rotate-90" : ""
                  }`}
                />
              </button>
              <div
                className={`grid transform-gpu transition-[grid-template-rows] duration-200 ${
                  isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                }`}
              >
                <div className="overflow-hidden">
                  <LessonListInCategory
                    categoryIdx={c.idx}
                    categoryName={c.name}
                    onOpenLesson={onOpenLesson}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LessonListInCategory({
  categoryIdx,
  categoryName,
  onOpenLesson,
}: {
  categoryIdx: number;
  categoryName: string;
  onOpenLesson: (
    lesson: KBLessonItemOut,
    categoryIdx: number,
    categoryName: string,
  ) => void;
}) {
  const q = useKBLessons(categoryIdx);
  if (q.isLoading) {
    return (
      <div className="px-3 py-3 text-center font-mono text-[0.65625rem] uppercase tracking-[0.08em] text-ink-dim">
        Завантажую…
      </div>
    );
  }
  if (q.isError) {
    return (
      <div className="px-3 py-3 text-[0.75rem] text-danger">
        {errorText(q.error)}
      </div>
    );
  }
  const list = q.data!.items;
  if (list.length === 0) {
    return (
      <div className="px-3 py-3 text-[0.75rem] text-ink-dim">
        Уроків в цій категорії ще немає.
      </div>
    );
  }

  return (
    <div className="px-2 pb-2">
      {list.map((l) => (
        <button
          key={l.id}
          type="button"
          onClick={() => onOpenLesson(l, categoryIdx, categoryName)}
          className="flex w-full items-start gap-2 rounded-[0.625rem] px-2 py-2 text-left active:bg-bg"
        >
          {/* Переглянутий урок помічаємо галочкою (owner 2026-09-17:
              «насчет пометки просмотрено тоже делаем»).
              Іконку центруємо по ПЕРШОМУ РЯДКУ назви, а не відступом зверху:
              обгортка має рівно висоту рядка (0.8125rem × 1.375 ≈ 1.117rem),
              тому на одно- і дворядкових назвах вона стоїть на одній лінії з
              текстом (owner 2026-09-17: «кружечки з назвами уроків нихуя не
              вирівняні»). */}
          {/* ⚠️ Центрувати іконку по РЯДКУ недостатньо: у нашого шрифта фарба
              (великі літери) сидить нижче центру рядка, тому іконка виглядала
              вище за текст — owner 2026-09-17 двічі це побачив. Замір на
              стенді (DPR 3): фарба тексту була на 1.77 px нижче фарби іконки,
              це 0.136em від кеглю назви. Разом із попереднім −0.07em
              лишається +0.066em. Зсув у em → однаковий на будь-якому екрані.
              Мірити треба ФАРБУ (canvas actualBoundingBox), а не рядок. */}
          <span className="grid h-[1.117rem] w-3 shrink-0 place-items-center text-[0.8125rem] translate-y-[0.066em]">
            <Icon
              name={l.viewed ? "check" : "play"}
              size={12}
              color={l.viewed ? "#3F9E63" : "#FE6B04"}
            />
          </span>
          <div className="min-w-0 flex-1">
            <div
              className={
                "text-[0.8125rem] leading-snug " +
                (l.viewed ? "text-ink-dim" : "text-ink")
              }
            >
              {l.title}
            </div>
            {l.speaker_info && (
              <div className="mt-0.5 text-[0.6875rem] text-ink-dim line-clamp-1">
                {l.speaker_info}
              </div>
            )}
          </div>
          {l.duration && (
            <div className="font-mono text-[0.625rem] uppercase tracking-wider text-ink-dim shrink-0">
              {l.duration}
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

// ============================================================
// Lesson detail — editorial style
// ============================================================

//: Скільки секунд відтворення = «переглянуто» (owner 2026-09-17). Те саме
//: число стоїть на сервері у knowledge_base.VIEW_SECONDS.
const VIEW_SECONDS = 15;

function LessonDetailView({
  lessonId,
  fallback,
  categoryIdx,
  onBack,
}: {
  lessonId: number;
  fallback: KBLessonItemOut | null;
  categoryIdx: number | null;
  onBack: () => void;
}) {
  const q = useKBLessons(categoryIdx);
  const qAll = useKBLessonsAll(categoryIdx == null);
  const fresh = (q.data ?? qAll.data)?.items.find((l) => l.id === lessonId);
  const lesson = fresh ?? fallback;

  const voteMut = useVoteKBLesson();
  const markViewed = useMarkKBLessonViewed();
  //: Відмітку «переглянуто» шлемо один раз на відкритий урок.
  const markedRef = useRef(false);
  useEffect(() => {
    markedRef.current = false;
  }, [lessonId]);
  const [optimisticRating, setOptimisticRating] = useState<number | null>(null);
  const myRating = optimisticRating ?? lesson?.my_rating ?? 0;

  const handleVote = async (rating: number) => {
    if (voteMut.isPending) return;
    setOptimisticRating(rating);
    tg()?.HapticFeedback.selectionChanged();
    try {
      const r = await voteMut.mutateAsync({ lessonId, rating });
      tg()?.HapticFeedback.notificationOccurred("success");
      if (r.bonus_credited) {
        showAppAlert(
          `Ти поставив ${rating}/5\n\n+5 ₴ бонусів зараховано на квест-баланс — їх можна витратити на оплату підписки.`,
          { title: "Дякуємо за оцінку", emoji: "🧡" },
        );
      } else {
        showAppAlert(`Тепер ${rating}/5`, { title: "Оцінка оновлена" });
      }
    } catch (e) {
      setOptimisticRating(null);
      tg()?.HapticFeedback.notificationOccurred("error");
      showAppAlert(`Помилка: ${(e as Error).message}`);
    }
  };

  // Синхронізуємо optimistic state з freshly-fetched data в effect'і,
  // НЕ під час рендеру (інакше React 18 кричить "Cannot update
  // component while rendering").
  useEffect(() => {
    if (
      optimisticRating !== null &&
      fresh &&
      fresh.my_rating === optimisticRating
    ) {
      setOptimisticRating(null);
    }
  }, [fresh, optimisticRating]);

  // Скрол вгору при відкритті уроку. Layout робить ScrollToTop тільки на
  // зміну route, а список↔урок у КБ — внутрішній стейт (той самий
  // /knowledge-base), тому без цього урок відкривався на позиції списку
  // (а оскільки сторінка коротша — скрол прибивало вниз, відео не видно).
  // Юзер 2026-06-12: «сторінка відкривається з самого низу».
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [lessonId]);

  // Урок відкритий диплінком: поки список не приїхав — спінер; якщо приїхав,
  // а уроку немає — чесна помилка замість білого екрану.
  if (!lesson) {
    const loading = q.isLoading || qAll.isLoading;
    return (
      <>
        <BackNav onBack={onBack} showMenu />
        {loading ? <Spinner /> : <CenteredError message="Урок не знайдено" />}
      </>
    );
  }

  return (
    <>
      <BackNav onBack={onBack} showMenu />

      {/* Брівка на тій самій висоті, що скрізь (owner 2026-09-12). */}
      <div className="mb-3 flex items-center gap-2 px-1 pt-4 font-mono text-[0.65625rem] uppercase tracking-[0.08em] text-ink-dim">
        <span className="h-1.5 w-1.5 rounded-full bg-primary" />
        {lesson.category_label ?? "Урок"}
        {lesson.duration && (
          <>
            <span>·</span>
            <span className="inline-flex items-center gap-1">
              <Icon name="clock" size={12} />
              {lesson.duration}
            </span>
          </>
        )}
      </div>

      <h1
        className="mb-4 font-display font-bold text-ink"
        style={{ fontSize: "1.625rem", lineHeight: 1.1, letterSpacing: "-0.02em" }}
      >
        {lesson.title}
      </h1>

      <div className="mb-6 overflow-hidden rounded-ios-card bg-black shadow-card-soft">
        {lesson.video_url ? (
          <video
            key={lesson.id}
            src={lesson.video_url}
            controls
            preload="metadata"
            className="block w-full"
            onTimeUpdate={(e) => {
              // Перегляд зараховуємо після 15 секунд фактичного відтворення
              // (owner 2026-09-17). Шлемо ОДИН раз на урок: далі реф тримає
              // відмітку, а сервер усе одно ідемпотентний.
              if (markedRef.current) return;
              const seconds = Math.floor(e.currentTarget.currentTime);
              if (seconds < VIEW_SECONDS) return;
              markedRef.current = true;
              markViewed.mutate({ lessonId: lesson.id, seconds });
            }}
          />
        ) : (
          <div className="bg-surface-soft p-6 text-center">
            <div className="font-display text-[0.8125rem] font-semibold text-ink">
              Відео готується
            </div>
            <div className="mt-1 font-mono text-[0.65625rem] uppercase tracking-[0.08em] text-ink-dim">
              скоро з'явиться
            </div>
          </div>
        )}
      </div>

      {/* PDF та інші файли під відео (owner 2026-10-21). */}
      <LessonMaterialsCard materials={lesson.materials} />

      {lesson.speaker_info && (
        <Section title="Спікер">
          <div className="flex items-start gap-3 p-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-surface-soft text-primary">
              <Icon name="user" size={16} />
            </div>
            <div className="min-w-0 flex-1">
              <div
                className="font-serif italic text-ink"
                style={{ fontSize: "0.9375rem", lineHeight: 1.4 }}
                onClick={openExternalOnAnchorClick}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(lesson.speaker_info) }}
              />
              {lesson.speaker_url && (
                <a
                  href={lesson.speaker_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={openExternalOnAnchorClick}
                  className="mt-1.5 inline-flex items-center gap-1 font-mono text-[0.65625rem] uppercase tracking-[0.08em] text-primary"
                >
                  Профіль <Icon name="arrow-right" size={12} className="-rotate-45" />
                </a>
              )}
            </div>
          </div>
        </Section>
      )}

      {lesson.description && (
        <Section title="Про урок">
          <div
            className="p-4 text-[0.875rem] leading-relaxed text-ink [&_a]:text-primary [&_a]:underline"
            onClick={openExternalOnAnchorClick}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(lesson.description) }}
          />
        </Section>
      )}

      {/* Під уроками засновника — його Instagram (owner 2026-10-19). */}
      {lesson.category_label === FOUNDER_LESSONS_LABEL && <FounderInstagramCard />}

      <Section title="Оцінити урок">
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => {
                const filled = n <= myRating;
                return (
                  <button
                    key={n}
                    type="button"
                    disabled={voteMut.isPending}
                    onClick={() => handleVote(n)}
                    className={`transition-all duration-150 ${
                      filled ? "scale-100 opacity-100" : "scale-90 opacity-60"
                    } active:scale-75 disabled:cursor-wait`}
                    aria-label={`Оцінити на ${n}`}
                  >
                    <Icon
                      name={filled ? "star-fill" : "star"}
                      size={32}
                      color={filled ? "#FE6B04" : "rgba(23,20,17,0.30)"}
                    />
                  </button>
                );
              })}
            </div>
            {lesson.rating_count > 0 && lesson.average_rating && (
              <div className="text-right">
                <div
                  className="font-display font-bold text-ink"
                  style={{ fontSize: "1.375rem", lineHeight: 1 }}
                >
                  {lesson.average_rating.toFixed(1)}
                </div>
                <div className="mt-0.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] text-ink-dim">
                  {lesson.rating_count}{" "}
                  {lesson.rating_count === 1 ? "оцінка" : "оцінок"}
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 border-t border-line pt-3 text-[0.75rem] leading-relaxed text-ink-dim">
            {myRating > 0 ? (
              <>
                Твоя оцінка —{" "}
                <span className="font-display font-semibold text-ink">
                  {myRating}/5
                </span>
                . Можеш змінити будь-коли.
              </>
            ) : (
              <>
                За те, що ти оцінюєш кожен урок — даруємо{" "}
                <span className="font-display font-semibold text-primary">
                  +5 ₴
                </span>{" "}
                бонусів. Накопичуй і використай на оформлення підписки.
              </>
            )}
          </div>
        </div>
      </Section>

    </>
  );
}
